import React, { useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Navbar as BsNavbar, Nav, Button, Container, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '@rd-web-markets/shared/dist/store/features/authSlice';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { UserService } from '@rd-web-markets/shared/dist/services/user.service';
import logo from '@assets/images/logo.png';
import HelpModal from '@rd-web-markets/shared/dist/navbar/HelpModal';
import flag from './flag.png';
import ToReleaseNotesButton from 'src/pages/shared/release_notes/ToReleaseNotesButton';
import SearchBar from '@components/util/SearchBar';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
const templateLinks = [{
  text: 'Claim Templates',
  link: 'claim_template'
}, {
  text: 'R&D Report Template',
  link: 'report_templates?template_type=rnd'
}, {
  text: 'AIF Report Template',
  link: 'report_templates?template_type=aif'
}, {
  text: 'Questionnaires',
  link: 'questionnaires'
}, {
  text: 'FAQ',
  link: 'faq_template'
}, {
  text: 'How To',
  link: 'how_to_template'
}, {
  text: 'Checklist',
  link: 'checklist_template'
}];
export default function NavbarV2(_ref) {
  let {
    basename,
    match,
    userRole
  } = _ref;
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => state.auth.user);
  const [modalOpen, setModalOpen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templatesPanelTop, setTemplatesPanelTop] = useState(0);
  const [templatesPanelLeft, setTemplatesPanelLeft] = useState(0);
  const templatesRowRef = useRef(null);
  const breadcrumb = useSelector(state => state.navbarBreadcrumb);
  const onLogOut = e => {
    e.preventDefault();
    UserService.logout().then(() => {
      dispatch(logout());
      history.replace('/login');
    }).catch(err => dispatch(handleError(err.description)));
  };
  const navSearch = query => {
    if (!query) return;
    history.push(`/${userRole}/v2/search?query=${encodeURIComponent(query)}`);
  };
  const onTemplatesClick = e => {
    e.stopPropagation();
    if (templatesRowRef.current) {
      const rect = templatesRowRef.current.getBoundingClientRect();
      setTemplatesPanelTop(rect.top);
      setTemplatesPanelLeft(rect.right + 4);
    }
    setShowTemplates(prev => !prev);
  };
  const isAdmin = userRole === 'admin';
  const navLinks = [{
    dataTestId: 'nav-link-home',
    label: 'Home',
    path: `/${userRole}`,
    icon: 'home'
  }, {
    dataTestId: 'nav-link-claims',
    label: 'Claims',
    path: isAdmin ? `/${userRole}/all_claims?order_by=companies.name%20asc%2C%20to_date%20desc&page=1&query=&type=active` : `/${userRole}/my_claims`,
    icon: 'description'
  }, {
    dataTestId: 'nav-link-companies',
    label: 'Companies',
    path: `/${userRole}/companies?page=1`,
    icon: 'business'
  }];
  const adminLinks = [{
    dataTestId: 'nav-link-users',
    label: t('users') || 'Users',
    path: `/${userRole}/users`,
    icon: 'group'
  }, {
    dataTestId: 'nav-link-sic-codes',
    label: t('sic_codes') || 'Sic Code',
    path: `/${userRole}/sic_codes`,
    icon: 'privacy_tip'
  }, {
    dataTestId: 'nav-link-allowed-characters',
    label: t('allowed_characters') || 'Allowed Characters',
    path: `/${userRole}/allowed_characters`,
    icon: 'token'
  }, {
    dataTestId: 'nav-link-rates',
    label: t('rates') || 'Rates',
    path: `/${userRole}/rates`,
    icon: 'trending_up'
  }, {
    dataTestId: 'nav-link-translations',
    label: t('translations') || 'Translations',
    path: `/${userRole}/translations`,
    icon: 'language'
  }];

  // Highlights a nav/dropdown item as active when its base path matches the
  // current route. Query params are stripped from `path` before comparing
  // because links like "Claims" include filter params (order_by, page, etc.)
  // that vary, but should still count as active for that route.
  const isActive = path => location.pathname === path.split('?')[0];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HelpModal, {
    show: modalOpen,
    onHide: () => setModalOpen(false)
  }), showTemplates && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    onClick: () => setShowTemplates(false),
    style: {
      position: 'fixed',
      inset: 0,
      zIndex: 1099
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'fixed',
      top: templatesPanelTop,
      left: templatesPanelLeft,
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      boxShadow: '4px 4px 16px rgba(0,0,0,0.12)',
      zIndex: 1100,
      minWidth: '200px',
      overflow: 'hidden'
    },
    onClick: e => e.stopPropagation()
  }, templateLinks.map(item => /*#__PURE__*/React.createElement("div", {
    key: item.link,
    onClick: () => {
      history.push(`/${userRole}/${item.link}`);
      setShowTemplates(false);
    },
    style: {
      padding: '0.65rem 1.25rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      color: '#333',
      borderBottom: '1px solid #f0f0f0'
    },
    onMouseEnter: e => e.currentTarget.style.background = '#f5f9fc',
    onMouseLeave: e => e.currentTarget.style.background = 'transparent'
  }, item.text)))), /*#__PURE__*/React.createElement(BsNavbar, {
    sticky: "top",
    expand: "md",
    className: "navbar-v2"
  }, /*#__PURE__*/React.createElement(Container, {
    fluid: true,
    className: "navbar-container px-3"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    className: "me-2 d-flex align-items-center",
    onToggle: open => {
      if (!open) setShowTemplates(false);
    }
  }, /*#__PURE__*/React.createElement(OverlayTrigger, {
    placement: "bottom",
    overlay: /*#__PURE__*/React.createElement(Tooltip, null, "Menu")
  }, /*#__PURE__*/React.createElement(Dropdown.Toggle, {
    as: Button,
    variant: "text",
    className: "p-1 border-0 bg-transparent d-flex align-items-center",
    id: "v2-nav-hamburger",
    "data-testid": "v2-nav-hamburger",
    style: {
      lineHeight: 1
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    style: {
      fontSize: '1.5rem'
    }
  }, "menu"))), /*#__PURE__*/React.createElement(Dropdown.Menu, {
    className: "shadow",
    style: {
      minWidth: '200px'
    }
  }, navLinks.map(_ref2 => {
    let {
      dataTestId,
      label,
      path,
      icon
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.Item, {
      key: label,
      onClick: () => history.push(path),
      active: isActive(path),
      className: "d-flex align-items-center gap-2",
      "data-testid": dataTestId
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons md-18 me-2"
    }, icon), label);
  }), user?.account_type === 'admin' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.Divider, null), adminLinks.map(_ref3 => {
    let {
      dataTestId,
      label,
      path,
      icon
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.Item, {
      key: label,
      onClick: () => history.push(path),
      active: isActive(path),
      className: "d-flex align-items-center gap-2",
      "data-testid": dataTestId
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons md-18 me-2"
    }, icon), label);
  }), /*#__PURE__*/React.createElement(Dropdown.Item, {
    ref: templatesRowRef,
    onClick: onTemplatesClick,
    className: "d-flex align-items-center justify-content-between",
    active: showTemplates
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18 me-2"
  }, "source"), "Templates"), /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    style: {
      fontSize: '1rem'
    }
  }, "chevron_right"))))), breadcrumb.label && /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center ms-3",
    style: {
      fontSize: '0.9rem'
    }
  }, /*#__PURE__*/React.createElement(Link, {
    to: breadcrumb.path,
    style: {
      color: '#0078B9',
      textDecoration: 'none',
      fontWeight: 500
    }
  }, breadcrumb.label), /*#__PURE__*/React.createElement("span", {
    className: "ms-2 text-muted"
  }, "/")), /*#__PURE__*/React.createElement("div", {
    className: "navbar-v2-search mx-auto",
    style: {
      width: '100%',
      maxWidth: '460px',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      top: '50%',
      marginTop: '-20px'
    }
  }, /*#__PURE__*/React.createElement(SearchBar, {
    onSubmit: navSearch,
    placeholder: "Search by Claim or Company"
  })), /*#__PURE__*/React.createElement(BsNavbar.Toggle, {
    "aria-controls": "navbar-v2-right"
  }), /*#__PURE__*/React.createElement(BsNavbar.Collapse, {
    id: "navbar-v2-right",
    className: "justify-content-end"
  }, /*#__PURE__*/React.createElement(Nav, {
    className: "navbar-right align-items-center"
  }, /*#__PURE__*/React.createElement(OverlayTrigger, {
    placement: "bottom",
    overlay: /*#__PURE__*/React.createElement(Tooltip, null, "Log out")
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: onLogOut
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "logout"))), /*#__PURE__*/React.createElement(ToReleaseNotesButton, null), /*#__PURE__*/React.createElement(OverlayTrigger, {
    placement: "bottom",
    overlay: /*#__PURE__*/React.createElement(Tooltip, null, user?.first_name, " ", user?.last_name)
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    className: "p-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    style: {
      fontSize: '1.5rem'
    }
  }, "account_circle"))), /*#__PURE__*/React.createElement(Dropdown, {
    align: "end",
    className: "ms-2 d-flex align-items-center"
  }, /*#__PURE__*/React.createElement(OverlayTrigger, {
    placement: "bottom",
    overlay: /*#__PURE__*/React.createElement(Tooltip, null, "Help")
  }, /*#__PURE__*/React.createElement(Dropdown.Toggle, {
    as: Button,
    variant: "text",
    className: "p-0 border-0 bg-transparent d-flex align-items-center",
    style: {
      lineHeight: 1
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18 blue",
    style: {
      verticalAlign: 'middle'
    }
  }, "question_mark"))), /*#__PURE__*/React.createElement(Dropdown.Menu, {
    className: "shadow fade",
    align: "end"
  }, /*#__PURE__*/React.createElement(Dropdown.Item, {
    onClick: () => history.push(`/${userRole}/how_to`)
  }, "How To"), /*#__PURE__*/React.createElement(Dropdown.Item, {
    onClick: () => history.push(`/${userRole}/report_issues`)
  }, "Report Issue"), /*#__PURE__*/React.createElement(Dropdown.Item, {
    onClick: () => setModalOpen(true)
  }, "FAQ"))), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center ms-3"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: '1px',
      height: '32px',
      backgroundColor: '#dee2e6',
      marginRight: '12px'
    }
  }), /*#__PURE__*/React.createElement("img", {
    src: flag,
    alt: "Ayming Flag",
    height: "30",
    className: "me-3"
  }), /*#__PURE__*/React.createElement(BsNavbar.Brand, {
    href: `/${userRole}/v2`,
    className: "mb-0"
  }, /*#__PURE__*/React.createElement("img", {
    src: logo,
    alt: "Ayming Logo",
    height: "36"
  }))))))));
}